import React, { useCallback, useEffect, useState } from 'react'
import classnames from 'classnames'
import copy from 'copy-to-clipboard'
import message from 'antd/lib/message'
import moment from 'moment'

import { PrimaryButton, UikSocialHeader } from 'components'
import { apiManager, constants } from 'helpers'
import { useMutation } from 'hooks'
import HostPortalListItem from './components/HostPortalListItem'
import HostPortalStatCard from './components/HostPortalStatCard'
import HostPortalGenderRatio from './components/HostPortalStatCard/HostPortalGenderRatio'
import PartnerPortalPage from './components/PartnerPortalPage'
import cfg from 'config'
import cls from './PartnerPortal.module.scss'
import EventDetails from './components/EventDetails'
import FreeTicketModal from './components/FreeTicketModal'
import EventGallery from 'app/Host/PartnerPortal/components/EventGallery'

const {
  EVENT_CONSTANTS: {
    STATUS: { CREATED },
  },
  HOST_PORTAL_TABS: { TICKET_SALES },
  ROUTES: { HOST_PORTAL, LANDING },
} = constants

const PartnerPortalEventPage = ({
  match: {
    params: { eventId },
  },
  user,
  history,
}) => {
  const [eventHasStarted, setEventHasStarted] = useState(false)
  const [eventIsPending, setEventIsPending] = useState(false)
  const [attendees, setAttendees] = useState([])
  const [event, setEvent] = useState()
  const [numOfMatches, setNumOfMatches] = useState(0)
  const [selectedTabTitle, setSelectedTabTitle] = useState(TICKET_SALES)
  const [eventEarnings, setEventEarnings] = useState(0)
  const [isPaidEvent, setIsPaidEvent] = useState(false)
  const [showFreeTicketModal, setShowFreeTicketModal] = useState(false)
  const eventLink = `${cfg.main_domain}/events/${event?.slug}`

  const getPartnerPortalEventPageData = useCallback(
    async eventId => {
      try {
        const { event, totalSales } = await apiManager.getPartnerPortalEventPageData(eventId)
        setEvent(event)
        const attendees = event.Signups.map(signup => signup.signedUpByUser)
        setAttendees(attendees)
        setIsPaidEvent(event.ticketPrice > 0)
        setEventIsPending(event.status === 'Pending')
        setEventEarnings(totalSales)
        setNumOfMatches(event.Matches.length)
        const eventDate = moment(event.startTime)
        const currDate = moment()
        setEventHasStarted(currDate >= eventDate)
      } catch (err) {
        if (err.message === 'Not Found') {
          return history.push(LANDING)
        }
      }
    },
    [history]
  )

  const isCreated = event?.status === CREATED

  const generateSchedule = useMutation(apiManager.generateSchedule)

  const deleteSchedule = useMutation(apiManager.deleteSchedule)

  useEffect(() => {
    if (user?.id && eventId) {
      getPartnerPortalEventPageData(eventId)
    }
  }, [user, eventId, getPartnerPortalEventPageData])

  const selectTabTitle = selectedTabTitle => {
    setSelectedTabTitle(selectedTabTitle)
  }

  const deleteSignup = async (userId, refund) => {
    const response = await apiManager.deleteSignup({ userId, eventId, refund })
    if (response.status === 'ok') {
      message.success('Signup cancelled. User has been refunded if this was a paid event')
      getPartnerPortalEventPageData(eventId)
    } else {
      message.error('Unable to cancel user signup')
    }
  }

  const { eventPage, pageContainer, eventPageIsVisible, pageIsVisible } = cls
  return (
    <PartnerPortalPage>
      <div
        className={classnames(
          eventPage,
          pageContainer,
          eventPageIsVisible,
          pageIsVisible,
          'bg-white'
        )}
      >
        <div className={cls.eventBackButtonContainer}>
          <button
            className={cls.hostPortalBackButton}
            onClick={() => history.push(HOST_PORTAL)}
            type="button"
          >
            Back To Portal
          </button>
        </div>
        <div className={cls.pageTitle}>Event Manager</div>
        <UikSocialHeader
          className={cls.hostPortalSocialHeader}
          imgUrl={event?.image}
          name={event?.title ?? 'Loading...'}
          selectedTabTitle={selectedTabTitle}
          selectTabTitle={tabTitle => selectTabTitle(tabTitle)}
          margin="true"
          hidesEditTab={true}
        />
        <div className="relative">
          <div className="absolute right-12 -top-12 flex w-1/2 justify-end items-center">
            {isCreated && (
              <PrimaryButton className="text-sm" onClick={() => setShowFreeTicketModal(true)}>
                Give Free Ticket
              </PrimaryButton>
            )}
          </div>
        </div>
        {selectedTabTitle === TICKET_SALES && (
          <div className={cls.hostPortalAttendingTab}>
            <div className={cls.hostPortalOverview}>
              <HostPortalGenderRatio tickets={event?.tickets} />
              {!eventHasStarted && <EventData event={event} eventLink={eventLink} user={user} />}
              {isPaidEvent && (
                <HostPortalStatCard statTitle="My Earnings" statValue={`$${eventEarnings}`} />
              )}
              {eventHasStarted && (
                <HostPortalStatCard statTitle="Matches" statValue={numOfMatches} />
              )}
            </div>
            <EventGallery event={event} />
            {!eventIsPending && !!attendees.length && (
              <div className={cls.guestListContainer}>
                {isCreated && (
                  <>
                    {event.endTime ? (
                      <PrimaryButton
                        className="text-sm"
                        loading={deleteSchedule.isLoading}
                        onClick={async () => {
                          await deleteSchedule.mutateAsync(eventId)
                          await getPartnerPortalEventPageData(eventId)
                        }}
                      >
                        Delete Schedule
                      </PrimaryButton>
                    ) : (
                      <PrimaryButton
                        className="text-sm"
                        loading={generateSchedule.isLoading}
                        onClick={async () => {
                          await generateSchedule.mutateAsync(eventId)
                          await getPartnerPortalEventPageData(eventId)
                        }}
                      >
                        Generate Schedule
                      </PrimaryButton>
                    )}
                  </>
                )}
                <div>
                  {attendees.map(attendee => (
                    <HostPortalListItem
                      attendee={attendee}
                      isBachelorette={attendee.id === event.bachelorette}
                      isPaidEvent={isPaidEvent}
                      eventStatus={event.status}
                      deleteSignup={deleteSignup}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <FreeTicketModal
        eventId={eventId}
        show={showFreeTicketModal}
        onClose={reload => {
          setShowFreeTicketModal(false)
          if (reload) {
            getPartnerPortalEventPageData(eventId)
          }
        }}
      />
    </PartnerPortalPage>
  )
}

const EventData = ({ eventLink, event, user }) => (
  <div className={classnames(cls.guestlistStatsSingle, 'py-5')}>
    {event && (
      <EventDetails
        event={event}
        user={user}
        eventLink={eventLink}
        classNames={{ td: 'border-black' }}
      />
    )}
    <button
      type="button"
      className={cls.copyLinkBtn}
      onClick={() => {
        copy(eventLink)
        message.success('Link copied', 2.5)
      }}
    >
      Copy event link
    </button>
  </div>
)

export default PartnerPortalEventPage
